import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { endpoint } from '../resource/Constant';
import {
  Container,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
  FormControl
} from '@mui/material';
import { FileDownload } from '@mui/icons-material';
import jsPDF from 'jspdf';
import { marked } from 'marked';

// Define the exact section titles as they should appear *without* Markdown or emojis in the parsed object keys.
const SECTION_TITLES = [
  'a. Lesson Overview',
  'b. Learning Objectives',
  'c. Topic Concepts (in simple language)',
  'd. Gamification Ideas',
  'e. DIY Experiments or Activities',
  'f. Activities & Exercises',
  'g. Summary Notes for Students',
  'h. Worksheets',
  'i. Assessment',
  'j. Homework',
  'k. Teacher Tips',
  'l. Teaching Aids & Materials'
];

/**
 * Parses a given text into a dictionary of sections based on predefined titles.
 * Prioritizes the last occurrence of a section if duplicated.
 * @param {string} text - The full text response from the API.
 * @returns {Object} An object where keys are section titles and values are their content.
 */
function parseSectionsFromText(text) {
  const sections = {};
  const lines = text.split('\n');
  let currentSection = null;
  let currentContent = [];

  const normalizeLine = (line) => {
    return line
      .replace(/##\s*/g, '')
      .replace(/\*{1,2}/g, '')
      .replace(/🧾|🎯|📚|🎮|🧪|📝|📒|📄|🧠|🏡|🧑‍🏫|🧰/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const normalizedLine = normalizeLine(line);
    let foundSection = false;

    for (const sectionTitle of SECTION_TITLES) {
      if (normalizedLine === sectionTitle.trim()) {
        // If we found a new section, save the previous one's content
        if (currentSection !== null) {
          sections[currentSection] = currentContent.join('\n').trim();
        }
        currentSection = sectionTitle;
        currentContent = [];
        foundSection = true;
        break;
      }
    }

    if (!foundSection && currentSection !== null) {
      // Only add non-empty lines to content
      if (line.trim() !== '') {
        currentContent.push(line);
      }
    }
    // Ignore lines before the first identified section or empty lines when no section is active
  }

  // Save the content of the last section
  if (currentSection !== null) {
    sections[currentSection] = currentContent.join('\n').trim();
  }

  // Clean up content and remove unwanted comments/patterns (like "Example - Answer Key omitted for brevity")
  Object.keys(sections).forEach(key => {
    let content = sections[key];
    // Remove lines that look like comments or instructions within the content
    content = content.split('\n')
      .filter(line => !line.trim().match(/^\(.*\)$/) && !line.trim().match(/^\/\/.*$/))
      .join('\n')
      .trim();
    sections[key] = content;
  });

  return sections;
}


function sanitizeFilename(name) {
  return (name || 'lesson-plan')
    .replace(/[\u0000-\u001F\u007F\\/:*?"<>|]+/g, '')
    .replace(/\s+/g, '_')
    .substring(0, 50);
}

function LessonPlanner() {
  const [formData, setFormData] = useState({
    topic: '',
    standard: '',
    board: '',
    language_preference: 'English', // Set default to English
    isConceptClarity: false,
    isActivityBased: false,
    isAssessmentFocused: false,
  });

  const [loading, setLoading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [error, setError] = useState(null);
  const [lessonPlanSections, setLessonPlanSections] = useState({});

  // This ref is no longer used for PDF generation, but can remain if you use it for other UI purposes.
  const lessonPlanRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setDownloadUrl('');
    setError(null);
    setLessonPlanSections({});

    try {
      const token = localStorage.getItem('school_token');
      if (!token) {
        setError('Authorization token not found. Please log in.');
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${endpoint}/api/ai/getresponse`,
        formData,
        {
          responseType: 'json',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const responseData = response.data;

      if (
        responseData &&
        responseData.candidates &&
        responseData.candidates[0] &&
        responseData.candidates[0].content &&
        responseData.candidates[0].content.parts
      ) {
        const parts = responseData.candidates[0].content.parts;
        let allText = '';
        parts.forEach(part => {
          allText += part.text + '\n';
        });

        const cleanText = allText.replace(/Generated by Guntech AI\s*\n*/g, '').trim();

        const sections = parseSectionsFromText(cleanText);

        if (Object.keys(sections).length > 0) {
          setLessonPlanSections(sections);
        } else {
          setError('No recognizable sections found in the AI response. Please check the response format and section titles.');
          console.warn('AI response did not contain recognizable sections. Raw text was:', allText);
        }
      } else {
        setError('Invalid response format from the server. Expected Gemini API structure.');
        console.error('Unexpected API response structure:', responseData);
      }
    } catch (err) {
      console.error('API call error:', err);
      if (err.response) {
        setError(`Server error: ${err.response.status} - ${err.response.data?.detail || err.response.statusText || 'Unknown error'}`);
      } else if (err.request) {
        setError('No response from server.');
      } else {
        setError('Failed to set up request: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const generatePdf = () => {
    if (Object.keys(lessonPlanSections).length === 0) {
      setError("Please generate the lesson plan first.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      let y = 10; // Initial Y position
      const margin = 14; // All sides margin in mm
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const maxLineWidth = pageWidth - 2 * margin; // Content width
      const lineHeight = 6; // Standard line height for body text (adjusted for better spacing)
      const paragraphSpacing = 3; // Space between paragraphs/lines

      // Helper function to add a new page if content overflows
      const checkPageBreak = (requiredSpace) => {
        if (y + requiredSpace > pageHeight - margin) {
          pdf.addPage();
          y = margin; // Reset Y to top margin
        }
      };

      // --- Add Main Title and Form Data Header ---
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      const mainTitle = `Lesson Plan for ${formData.topic || 'Untitled'} (Standard ${formData.standard || 'N/A'})`;
      const splitMainTitle = pdf.splitTextToSize(mainTitle, maxLineWidth);
      checkPageBreak(splitMainTitle.length * lineHeight + 20); // Estimate space for title and header info
      pdf.text(splitMainTitle, margin, y);
      y += splitMainTitle.length * lineHeight + 5;

      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Board/Curriculum: ${formData.board || 'N/A'}`, margin, y);
      y += lineHeight;
      pdf.text(`Language: ${formData.language_preference || 'English'}`, margin, y);
      y += 10; // Extra space after header info

      // Add a horizontal line
      pdf.setDrawColor(180, 180, 180);
      pdf.line(margin, y, pageWidth - margin, y);
      y += 10; // Space after line

      // --- Add Sections ---
      SECTION_TITLES.forEach(sectionTitle => {
        let content = lessonPlanSections[sectionTitle] || ''; // Initialize with empty string if no content

        // Only render section if content is not empty after filtering
        if (content.trim() === '') {
          return; // Skip this section if it has no content
        }

        checkPageBreak(2 * lineHeight + 5); // Ensure space for title + first line of content

        // Section Title
        pdf.setFontSize(14); // Slightly larger for section titles
        pdf.setFont('helvetica', 'bold');
        pdf.text(sectionTitle, margin, y);
        y += lineHeight * 1.5; // Space after section title

        // Content
        pdf.setFontSize(11); // Standard font size for content
        pdf.setFont('helvetica', 'normal');

        // Special handling for 'h. Worksheets' section in PDF
        if (sectionTitle === 'h. Worksheets') {
          const worksheetBlocks = [];
          let currentBlock = { type: 'intro', content: [] }; // For any text before the first worksheet

          // Split the content by worksheet and answer key markers
          const lines = content.split('\n');
          for (const line of lines) {
            const worksheetMatch = line.match(/^\s*\(Worksheet (\d+)\)\s*$/i);
            const answerKeyMatch = line.match(/^\s*\(Answer Key – Worksheet (\d+)\)\s*$/i);

            if (worksheetMatch) {
                // If we were collecting an answer key, finalize it
                if (currentBlock.type === 'answerKey') {
                    worksheetBlocks.push({ ...currentBlock, content: currentBlock.content.join('\n').trim() });
                }
                // If we were collecting questions, finalize them
                else if (currentBlock.type === 'questions') {
                    worksheetBlocks.push({ ...currentBlock, content: currentBlock.content.join('\n').trim() });
                }
                currentBlock = { type: 'questions', title: `Worksheet ${worksheetMatch[1]}`, content: [] };
            } else if (answerKeyMatch) {
                // Finalize the questions for the current worksheet
                if (currentBlock.type === 'questions') {
                    worksheetBlocks.push({ ...currentBlock, content: currentBlock.content.join('\n').trim() });
                }
                currentBlock = { type: 'answerKey', title: `Answer Key – Worksheet ${answerKeyMatch[1]}`, content: [] };
            } else {
                currentBlock.content.push(line);
            }
          }
          // Add the last collected block
          if (currentBlock.content.length > 0) {
              worksheetBlocks.push({ ...currentBlock, content: currentBlock.content.join('\n').trim() });
          }

          worksheetBlocks.forEach((block, blockIndex) => {
            if (block.type === 'intro' && block.content.trim() === '') {
              return; // Don't render empty intro
            }

            // Apply margin for worksheet blocks and add titles
            if (block.type === 'questions' || block.type === 'answerKey') {
                checkPageBreak(lineHeight * 2); // Space for title + some content
                pdf.setFontSize(12); // Smaller title for sub-sections
                pdf.setFont('helvetica', 'bold');
                pdf.text(block.title, margin + 5, y); // Indent sub-titles
                y += lineHeight * 1.2;
                pdf.setFontSize(11);
                pdf.setFont('helvetica', 'normal');
            }

            const blockLines = block.content.split('\n');
            let currentNumber = 0; // For numbering questions or answers

            blockLines.forEach(line => {
                let textToPrint = line;
                let currentXPos = margin;
                let indent = 0;

                const numberedItemMatch = line.match(/^\s*(\d+\.)\s*(.*)/);

                if (numberedItemMatch) {
                    currentNumber = parseInt(numberedItemMatch[1]);
                    textToPrint = numberedItemMatch[2];
                    currentXPos = margin + 8; // Indent for numbered lists
                    pdf.text(`${currentNumber}.`, margin, y); // Print number separately
                    indent = 8;
                } else {
                    currentXPos = margin;
                    indent = 0;
                }

                // Handle bold text within the line
                const parts = textToPrint.split(/(\*\*.*?\*\*|__.*?__)/g);
                let segmentXPos = currentXPos;

                const splitTextSegments = [];
                let currentSegmentParts = [];
                let currentSegmentWidth = 0;

                parts.forEach(part => {
                    const isBold = (part.startsWith('**') && part.endsWith('**')) || (part.startsWith('__') && part.endsWith('__'));
                    const actualText = isBold ? part.substring(2, part.length - 2) : part;
                    const words = actualText.split(' ');

                    for (let wordIndex = 0; wordIndex < words.length; wordIndex++) {
                        const word = words[wordIndex];
                        const space = (wordIndex === words.length - 1 && part === parts[parts.length - 1] && words.length === 1) ? '' : ' ';
                        const wordToMeasure = word + space;
                        const wordWidth = pdf.getTextWidth(wordToMeasure);

                        if (currentSegmentWidth + wordWidth > (maxLineWidth - indent) && currentSegmentWidth > 0) {
                            splitTextSegments.push({ parts: [...currentSegmentParts], width: currentSegmentWidth });
                            currentSegmentParts = [];
                            currentSegmentWidth = 0;
                        }
                        currentSegmentParts.push({ text: wordToMeasure, bold: isBold });
                        currentSegmentWidth += wordWidth;
                    }
                });
                if (currentSegmentParts.length > 0) {
                    splitTextSegments.push({ parts: [...currentSegmentParts], width: currentSegmentWidth });
                }

                splitTextSegments.forEach(segment => {
                    checkPageBreak(lineHeight);
                    segmentXPos = margin + indent;
                    segment.parts.forEach(p => {
                        pdf.setFont('helvetica', p.bold ? 'bold' : 'normal');
                        pdf.text(p.text, segmentXPos, y);
                        segmentXPos += pdf.getTextWidth(p.text);
                    });
                    y += lineHeight;
                });

                if (line.trim() === '') {
                    y += paragraphSpacing;
                }
            });
            y += 5; // Small space after each worksheet/answer key block
          });
        } else {
          // Existing logic for other sections (non-worksheet)
          const contentLines = content.split('\n');
          let orderedListCounter = 0;

          contentLines.forEach(line => {
            const isNumberedList = line.match(/^\s*(\d+\.)\s*(.*)/);
            const isBulletedList = line.match(/^\s*(\*|-)\s*(.*)/);

            if (isNumberedList || isBulletedList) {
              let prefix = '';
              let actualContent = line.trim();
              const listItemIndent = 8;
              const currentMaxLineWidth = maxLineWidth - listItemIndent;

              if (isNumberedList) {
                orderedListCounter++;
                prefix = `${orderedListCounter}. `;
                actualContent = isNumberedList[2];
              } else if (isBulletedList) {
                prefix = '• ';
                actualContent = isBulletedList[2];
              }

              const splitListItem = pdf.splitTextToSize(actualContent, currentMaxLineWidth);

              splitListItem.forEach((itemLine, index) => {
                checkPageBreak(lineHeight);
                pdf.setFont('helvetica', 'normal');

                const parts = itemLine.split(/(\*\*.*?\*\*|__.*?__)/g);
                let currentXPos = margin + listItemIndent;

                parts.forEach(part => {
                  if (part.startsWith('**') && part.endsWith('**') || part.startsWith('__') && part.endsWith('__')) {
                    pdf.setFont('helvetica', 'bold');
                    pdf.text(part.substring(2, part.length - 2), currentXPos, y, { maxWidth: currentMaxLineWidth - (currentXPos - (margin + listItemIndent)) });
                    currentXPos += pdf.getTextWidth(part.substring(2, part.length - 2));
                  } else {
                    pdf.setFont('helvetica', 'normal');
                    pdf.text(part, currentXPos, y, { maxWidth: currentMaxLineWidth - (currentXPos - (margin + listItemIndent)) });
                    currentXPos += pdf.getTextWidth(part);
                  }
                });

                if (index === 0) {
                  pdf.setFont('helvetica', 'normal');
                  pdf.text(prefix, margin + listItemIndent - pdf.getTextWidth(prefix), y);
                }
                y += lineHeight;
              });
              y += paragraphSpacing;
            } else {
              orderedListCounter = 0;

              const parts = line.split(/(\*\*.*?\*\*|__.*?__)/g);
              let lineParts = [];

              parts.forEach(part => {
                if (part.startsWith('**') && part.endsWith('**') || part.startsWith('__') && part.endsWith('__')) {
                  lineParts.push({ text: part.substring(2, part.length - 2), bold: true });
                } else {
                  lineParts.push({ text: part, bold: false });
                }
              });

              const segments = [];
              let currentSegment = [];
              let currentSegmentWidth = 0;

              for (const part of lineParts) {
                const words = part.text.split(' ');
                for (let w = 0; w < words.length; w++) {
                  const word = words[w];
                  const space = (w === words.length - 1 && part === lineParts[lineParts.length - 1] && words.length === 1) ? '' : ' ';
                  const wordToMeasure = word + space;
                  const wordWidth = pdf.getTextWidth(wordToMeasure);

                  if (currentSegmentWidth + wordWidth > maxLineWidth && currentSegmentWidth > 0) {
                    segments.push({ parts: [...currentSegment], width: currentSegmentWidth });
                    currentSegment = [];
                    currentSegmentWidth = 0;
                  }
                  currentSegment.push({ text: wordToMeasure, bold: part.bold });
                  currentSegmentWidth += wordWidth;
                }
              }
              if (currentSegment.length > 0) {
                segments.push({ parts: [...currentSegment], width: currentSegmentWidth });
              }

              segments.forEach(segment => {
                checkPageBreak(lineHeight);
                let segmentXPos = margin;
                segment.parts.forEach(p => {
                  pdf.setFont('helvetica', p.bold ? 'bold' : 'normal');
                  pdf.text(p.text, segmentXPos, y);
                  segmentXPos += pdf.getTextWidth(p.text);
                });
                y += lineHeight;
              });

              if (line.trim() === '') {
                y += paragraphSpacing;
              }
            }
          });
          y += 5;
        }
      });

      // --- Add Footer to all pages ---
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(9);
        pdf.setTextColor(150, 150, 150);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Generated by Guntech AI', pageWidth / 2, pageHeight - 8, { align: 'center' });
      }

      const pdfBlob = pdf.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      setDownloadUrl(url);

    } catch (err) {
      console.error("Error generating PDF:", err);
      setError("Failed to generate PDF. An error occurred: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [downloadUrl]);

  return (
    <Container maxWidth="md" sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        📝 Generate Lesson Plan
      </Typography>
      <Card sx={{ width: '100%', mb: 4 }}>
        <CardContent className="space-y-4">
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Topic"
              id="topic"
              name="topic"
              value={formData.topic}
              onChange={handleChange}
              required
              margin="normal"
            />
            <TextField
              fullWidth
              label="Standard/Grade"
              id="standard"
              name="standard"
              type="number"
              value={formData.standard}
              onChange={handleChange}
              required
              margin="normal"
            />
            <TextField
              fullWidth
              label="Board/Curriculum"
              id="board"
              name="board"
              value={formData.board}
              onChange={handleChange}
              margin="normal"
            />
            {/* Language Preference Dropdown */}
            <FormControl fullWidth margin="normal">
              <InputLabel id="language-preference-label">Language Preference</InputLabel>
              <Select
                labelId="language-preference-label"
                id="language_preference"
                name="language_preference"
                value={formData.language_preference}
                label="Language Preference"
                onChange={handleChange}
              >
                <MenuItem value="English">English</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              Focus Areas
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isConceptClarity}
                  onChange={handleChange}
                  name="isConceptClarity"
                  color="primary"
                />
              }
              label="Concept Clarity"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isActivityBased}
                  onChange={handleChange}
                  name="isActivityBased"
                  color="primary"
                />
              }
              label="Activity Based"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isAssessmentFocused}
                  onChange={handleChange}
                  name="isAssessmentFocused"
                  color="primary"
                />
              }
              label="Assessment Focused"
            />
            <Button
              fullWidth
              variant="contained"
              color="primary"
              type="submit"
              disabled={loading}
              sx={{ mt: 4 }}
              onClick={() => setDownloadUrl('')}
            >
              {loading ? (
                <>
                  <CircularProgress size={24} color="inherit" sx={{ mr: 2 }} />
                  Generating Lesson Plan...
                </>
              ) : (
                <>
                  <FileDownload sx={{ mr: 2 }} />
                  Generate Lesson Plan
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* On-screen display of lesson plan */}
      {Object.keys(lessonPlanSections).length > 0 && (
        <Card sx={{ width: '100%', mt: 4}}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Lesson Plan for {formData.topic || 'Your Topic'} (Standard {formData.standard || 'N/A'})
            </Typography>
            {SECTION_TITLES.map(sectionTitle => {
              const content = lessonPlanSections[sectionTitle];
              if (!content || content.trim() === '') {
                return null;
              }
              // Special handling for 'h. Worksheets' section
              if (sectionTitle === 'h. Worksheets') {
                const worksheetContent = content; // This is the raw text for the entire worksheets section
                const worksheetBlocks = [];
                let currentBlock = { type: 'intro', content: [] }; // For any text before the first worksheet

                // Split the content by worksheet and answer key markers
                const lines = worksheetContent.split('\n');
                for (const line of lines) {
                    const worksheetMatch = line.match(/^\s*\(Worksheet (\d+)\)\s*$/i);
                    const answerKeyMatch = line.match(/^\s*\(Answer Key – Worksheet (\d+)\)\s*$/i);

                    if (worksheetMatch) {
                        // If we were collecting an answer key, finalize it
                        if (currentBlock.type === 'answerKey') {
                            worksheetBlocks.push({ ...currentBlock, content: currentBlock.content.join('\n').trim() });
                        }
                        // If we were collecting questions, finalize them
                        else if (currentBlock.type === 'questions') {
                            worksheetBlocks.push({ ...currentBlock, content: currentBlock.content.join('\n').trim() });
                        }
                        currentBlock = { type: 'questions', title: `Worksheet ${worksheetMatch[1]}`, content: [] };
                    } else if (answerKeyMatch) {
                        // Finalize the questions for the current worksheet
                        if (currentBlock.type === 'questions') {
                            worksheetBlocks.push({ ...currentBlock, content: currentBlock.content.join('\n').trim() });
                        }
                        currentBlock = { type: 'answerKey', title: `Answer Key – Worksheet ${answerKeyMatch[1]}`, content: [] };
                    } else {
                        currentBlock.content.push(line);
                    }
                }
                // Add the last collected block
                if (currentBlock.content.length > 0) {
                    worksheetBlocks.push({ ...currentBlock, content: currentBlock.content.join('\n').trim() });
                }

                return (
                    <Box key={sectionTitle} sx={{ mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {sectionTitle}
                        </Typography>
                        {worksheetBlocks.map((block, index) => {
                            if (block.type === 'intro' && block.content.trim() === '') {
                                return null; // Don't render empty intro
                            }
                            if (block.type === 'intro') {
                                return (
                                    <Typography
                                        key={index}
                                        variant="body2"
                                        component="div"
                                        sx={{ whiteSpace: 'pre-line', mb: 1 }}
                                        dangerouslySetInnerHTML={{ __html: marked.parse(block.content) }}
                                    />
                                );
                            } else if (block.type === 'questions') {
                                return (
                                    <Box key={index} sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 2, p: 2 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            {block.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            component="div"
                                            sx={{ whiteSpace: 'pre-line' }}
                                            dangerouslySetInnerHTML={{ __html: marked.parse(block.content) }}
                                        />
                                    </Box>
                                );
                            } else if (block.type === 'answerKey') {
                                return (
                                    <Box key={index} sx={{ mt: 1, mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                            {block.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            component="div"
                                            sx={{ whiteSpace: 'pre-line' }}
                                            dangerouslySetInnerHTML={{ __html: marked.parse(block.content) }}
                                        />
                                    </Box>
                                );
                            }
                            return null;
                        })}
                    </Box>
                );
              } else {
                // Default rendering for other sections
                return (
                  <Box key={sectionTitle} sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {sectionTitle}
                    </Typography>
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{
                        whiteSpace: 'pre-line',
                        '& h1': { fontSize: '1.8rem', fontWeight: 'bold', mt: 2, mb: 1 },
                        '& h2': { fontSize: '1.5rem', fontWeight: 'bold', mt: 2, mb: 1 },
                        '& h3': { fontSize: '1.3rem', fontWeight: 'bold', mt: 2, mb: 1 },
                        '& strong': { fontWeight: 'bold' },
                        '& em': { fontStyle: 'italic' },
                        '& ul': { listStyleType: 'disc', pl: 3, mb: 1 },
                        '& ol': { listStyleType: 'decimal', pl: 3, mb: 1 },
                        '& li': { mb: 0.5 },
                        '& p': { mb: 1 }
                      }}
                      dangerouslySetInnerHTML={{
                        __html: marked.parse(content),
                      }}
                    />
                  </Box>
                );
              }
            })}
          </CardContent>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              variant="contained"
              color="success"
              sx={{ mt: 2, mr: 2 }}
              onClick={generatePdf}
              disabled={loading}
            >
              {loading ? (
                <>
                  <CircularProgress size={24} color="inherit" sx={{ mr: 2 }} />
                  Generating PDF...
                </>
              ) : (
                'Generate PDF'
              )}
            </Button>
            {downloadUrl && (
              <a
                href={downloadUrl}
                download={`${sanitizeFilename(formData.topic)}.pdf`}
                style={{ textDecoration: 'none' }}
              >
                <Button variant="contained" color="success" sx={{ mt: 2 }}>
                  Download as PDF
                </Button>
              </a>
            )}
          </Box>
        </Card>
      )}

      {error && (
        <Box sx={{ width: '100%', mt: 3, bgcolor: 'error.light', color: 'error.main', p: 2, borderRadius: 1 }}>
          <Typography variant="body2">
            <strong>Error:</strong> {error}
          </Typography>
        </Box>
      )}
    </Container>
  );
}

export default LessonPlanner;